import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Download, ExternalLink, ShieldAlert, LockKeyhole, LogOut } from "lucide-react";
import { GateShell } from "@/components/exam/ExamGates";

export const inSeb = () => typeof window !== "undefined" && (!!window.SafeExamBrowser || /\bSEB\//.test(navigator.userAgent || ""));
const QUIT = "/seb/quit";

// Reads the URL-hashed Config Key from the SEB JavaScript API (SEB 3.x).
function readConfigKey() {
  return new Promise((resolve) => {
    const sec = window.SafeExamBrowser?.security;
    if (!sec) return resolve("");
    if (sec.configKey) return resolve(sec.configKey);
    if (typeof sec.updateKeys === "function") { sec.updateKeys(() => resolve(window.SafeExamBrowser.security.configKey || "")); setTimeout(() => resolve(sec.configKey || ""), 8000); }
    else resolve("");
  });
}

export function SebLaunchGate({ candId, onExit }) {
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState(null);
  const open = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/candidates/${candId}/seb/launch`); setLinks(data); window.location.href = data.seb_url; }
    catch (e) { setLinks({ error: e.response?.data?.detail || "Could not prepare the test" }); }
    finally { setBusy(false); }
  };
  return (
    <GateShell testId="seb-launch-gate">
      <div className="mx-auto h-12 w-12 rounded-xl bg-primary/15 flex items-center justify-center"><LockKeyhole className="h-6 w-6 text-primary" /></div>
      <h1 className="text-2xl font-display font-bold">Open in Safe Exam Browser</h1>
      <p className="text-sm text-muted-foreground leading-relaxed">This test only runs inside the free Safe Exam Browser app for Windows, Mac or iPad. Install it once, then open your test with the button below. Your timer hasn't started.</p>
      <div className="grid gap-2.5">
        <Button variant="outline" asChild><a href="https://safeexambrowser.org/download_en.html" target="_blank" rel="noopener noreferrer" data-testid="seb-download"><Download className="h-4 w-4 mr-1.5" /> 1. Get Safe Exam Browser</a></Button>
        <Button onClick={open} disabled={busy} className="gap-1.5" data-testid="seb-open">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} 2. Open my test in Safe Exam Browser</Button>
      </div>
      {links?.error && <p className="text-xs text-rose-400" data-testid="seb-launch-error">{links.error}</p>}
      {links?.download_url && <p className="text-xs text-muted-foreground" data-testid="seb-fallback">Nothing happened? <a href={links.download_url} className="text-primary hover:underline" data-testid="seb-config-download">Download the test file</a> and open it.</p>}
      <p className="text-[11px] text-muted-foreground">Can't install it (for example on a Chromebook)? Ask the recruiter to let you take the test in Chrome or Edge instead.</p>
      <button onClick={onExit} className="text-xs text-muted-foreground hover:text-foreground" data-testid="seb-launch-exit">Exit</button>
    </GateShell>
  );
}

export function SebVerifyGate({ candId, onVerified }) {
  const [state, setState] = useState("checking");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    setState("checking");
    readConfigKey().then((hash) => api.post(`/candidates/${candId}/seb/verify`, { url: window.location.href.split("#")[0], config_key_hash: hash }))
      .then(() => { if (alive) onVerified(); })
      .catch(() => { if (alive) setState("failed"); });
    return () => { alive = false; };
  }, [candId, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <GateShell testId={state === "checking" ? "seb-verifying" : "seb-verify-failed"}>
      {state === "checking" ? (
        <><Loader2 className="h-8 w-8 text-primary animate-spin mx-auto" /><p className="text-sm text-muted-foreground">Checking Safe Exam Browser settings…</p></>
      ) : (
        <>
          <div className="mx-auto h-12 w-12 rounded-xl bg-rose-500/15 flex items-center justify-center"><ShieldAlert className="h-6 w-6 text-rose-400" /></div>
          <h1 className="text-xl font-display font-bold">Settings don't match this test</h1>
          <p className="text-sm text-muted-foreground">Safe Exam Browser isn't using the settings made for your test, or the file is out of date. Quit, then open the test again from your application.</p>
          <Button className="w-full" onClick={() => setAttempt((n) => n + 1)} data-testid="seb-verify-retry">Try again</Button>
          <Button asChild variant="outline" className="w-full gap-1.5"><a href={QUIT} data-testid="seb-quit-failed"><LogOut className="h-4 w-4" /> Quit Safe Exam Browser</a></Button>
        </>
      )}
    </GateShell>
  );
}
